import React from 'react';
import {
  accountTypeData,
  iconsImgPackage,
} from '@pages/user/checkout/v2/data.tsx';
import { TPackages } from '@src/types';

import { Step } from './Step';

interface StepProgressProps {
  current: number;
  total: number;
  platform?: string;
  accountType?: number;
  selectedPackage?: TPackages | null;
  onClick?: (step: number) => void;
  isAddons?: boolean;
  skipPlatform?: boolean;
  skipAccountSteps?: boolean; // For affiliate checkout: skip Account Type and Account Size
}

const renderPlatformTitle = (platform: string, defaultTitle: string) => {
  if (!platform) return <span>{defaultTitle}</span>;

  return (
    <div className="flex items-center gap-[10px]  mt-[5px]">
      {iconsImgPackage[platform as keyof typeof iconsImgPackage].icon}
      <span className="font-semibold text-[14px] text-white">
        {iconsImgPackage[platform as keyof typeof iconsImgPackage].text}
      </span>
    </div>
  );
};

const renderAccountTypeTitle = (
  accountType?: number,
  defaultTitle?: string
) => {
  if (!accountType) return <span>{defaultTitle}</span>;

  return (
    <div className="font-semibold text-[14px]">
      <span className="text-[14px] font-normal">
        {accountTypeData[(accountType || 1) - 1].title}
      </span>
    </div>
  );
};

const renderPackageTitle = (
  selectedPackage?: TPackages | null,
  defaultTitle?: string
) => {
  if (!selectedPackage) return <span>{defaultTitle}</span>;

  return (
    <div className="font-semibold text-[14px] break-words whitespace-pre-line max-w-[180px] truncate">
      {selectedPackage ? (
        (selectedPackage.display_name_app ?? '') + ' Account'
      ) : (
        <span className="text-[14px] font-normal">Select Base Account</span>
      )}
    </div>
  );
};

const renderAddonsTitle = (isAddons?: boolean, defaultTitle?: string) => {
  if (!isAddons) return <span>{defaultTitle}</span>;

  return <span>See details on this page</span>;
};

const renderReviewTitle = () => {
  return <span>Review & Checkout</span>;
};

const StepProgress: React.FC<StepProgressProps> = ({
  current,
  total,
  onClick,
  platform,
  accountType,
  isAddons,
  selectedPackage,
  skipPlatform,
  skipAccountSteps,
}) => {
  const allTitles = [
    renderPlatformTitle(platform || '', 'Select Platform'),
    renderAccountTypeTitle(accountType, 'Account Type'),
    renderPackageTitle(selectedPackage, 'Account Size'),
    renderAddonsTitle(isAddons, 'Add Ons'),
    renderReviewTitle(),
  ];

  const titleWithoutPlatForm = [
    renderAccountTypeTitle(accountType, 'Account Type'),
    renderPackageTitle(selectedPackage, 'Account Size'),
    renderAddonsTitle(isAddons, 'Add Ons'),
    renderReviewTitle(),
  ];

  // For affiliate checkout: Platform -> AddOns -> Review (skip Account Type and Account Size)
  const affiliateTitles = [
    renderPlatformTitle(platform || '', 'Select Platform'),
    renderAddonsTitle(isAddons, 'Add Ons'),
    renderReviewTitle(),
  ];

  const titleStep = (idx: number) => {
    if (skipAccountSteps) return affiliateTitles[idx];

    return skipPlatform ? titleWithoutPlatForm[idx] : allTitles[idx];
  };

  return (
    <div className="flex items-center w-full gap-3">
      <div className="flex-1 flex gap-[7px] bg-[#1F2630] pe-5">
        {Array.from({ length: total }).map((_, idx) => (
          <div
            key={idx}
            className="flex-1 cursor-pointer flex "
            onClick={() => onClick?.(idx)}
          >
            <Step
              status={
                idx < current - 1
                  ? 'completed'
                  : idx === current - 1
                    ? 'in progress'
                    : 'pending'
              }
              stepNumber={idx + 1}
              title={titleStep(idx)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default StepProgress;
